import { ElementType } from "react";

import {
  Box,
  IconButton,
  List as MUIList,
  ListItemButton as MUIListItemButton,
  Stack,
  styled,
} from "@mui/material";
import logoSrc from "assets/images/logo.png";
import shortLogoSrc from "assets/images/short-logo.png";
import { base, primary } from "configs/colors";
import { lineHeight, rem } from "utils";

import AppDivider from "../Divider";
import Link from "../Link";

export const Container = styled(Box)<{ collapsed: boolean }>(
  ({ collapsed }) => ({
    ".MuiPaper-root": {
      borderRight: 0,
      borderLeft: 0,
      width: collapsed ? "60px" : "280px",
      height: "100vh",
      backgroundColor: primary[700],
      outline: 0,
      transition:
        "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, background-color 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    },
  })
);

export const LogoWrapper = styled(Link)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingLeft: "8px",
  paddingRight: "8px",
}));

export const Logo = styled("div")<{ collapsed: boolean }>(({ collapsed }) => ({
  width: collapsed ? "34px" : "115px",
  height: "34px",
  cursor: "pointer",
  transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  backgroundColor: base.White,
  backgroundImage: `url('${collapsed ? shortLogoSrc : logoSrc}')`,
  backgroundPosition: "center",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  borderRadius: "4px",
  boxShadow:
    "0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)",
  "&:hover": {
    opacity: 0.7,
  },
}));

export const MenuContainer = styled(Box)(() => ({
  flex: 1,
  height: "100%",
  position: "relative",
  overflow: "hidden",
  marginTop: "-16px",
  marginBottom: "-16px",

  "&:hover .menu-scrollbar": {
    opacity: 1,
  },
}));

export const MenuWrapper = styled(Stack)(() => ({
  height: "inherit",
  gap: "4px",
  justifyContent: "space-between",
  overflow: "hidden auto",
  paddingTop: "16px",
  paddingBottom: "16px",

  scrollbarWidth: "none" /* Firefox */,
  scrollbarColor: "transparent transparent" /* Firefox */,

  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

export const MenuBarPlaceholder = styled(Box)(() => ({
  display: "var(--menu-bar-placeholder-display)",
  width: "100vw",
  height: "100vh",
  position: "fixed",
  zIndex: 100,
  inset: 0,
}));

export const MenuBarTrack = styled(Box)(() => ({
  "--menu-bar-placeholder-display": "none",
  "--menu-scrollbar-height": "0px",
  "--menu-scrollbar-top": "0px",
  position: "absolute",
  height: "inherit",
  overflow: "hidden",
  zIndex: 99,
  right: 0,
  bottom: 0,
  top: 0,
  width: "8px",
}));

export const MenuScrollbar = styled(Box)(() => ({
  width: "6px",
  height: "var(--menu-scrollbar-height, 0)",
  position: "absolute",
  borderRadius: "7px",
  zIndex: 99,
  top: 0,
  bottom: 0,
  right: 1,
  background: primary[400],
  transition:
    "opacity 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, background 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  opacity: 0,
  transform: "translate3d(0px, var(--menu-scrollbar-top, 0), 0px)",
}));

export const List = styled(MUIList)<{ component?: string }>(() => ({
  padding: "0px 8px",
  display: "flex",
  flexDirection: "column",
  rowGap: "4px",
}));

export const SubList = styled(Box)<{
  level: number;
  numberOfChildren: number;
}>(({ level, numberOfChildren }) => ({
  display: "flex",
  flexDirection: "column",
  rowGap: "4px",
  marginTop: "4px",
  position: "relative",
  "&:before":
    numberOfChildren > 1
      ? {
          content: '""',
          position: "absolute",
          zIndex: 2,
          top: "50%",
          transform: "translateY(-50%)",
          left: `${level * 12 + 4}px`,
          width: "1px",
          height: "calc(100% - 24px)",
          borderLeft: `1px dashed ${primary[100]}`,
        }
      : "none",
}));

export const ListItemButton = styled(MUIListItemButton)<{
  to?: string;
  component?: ElementType;
  active?: boolean;
  open?: boolean;
  level: number;
  collapsed: boolean;
}>(({ active, open, level, collapsed }) => ({
  borderRadius: "8px",
  backgroundColor: active ? primary[900] : "inherit",
  color: active ? base.White : primary[100],
  padding: "8px 12px",
  paddingLeft: `${level * 12}px`,
  whiteSpace: "nowrap",
  overflow: "hidden",
  columnGap: "12px",
  "&:hover": {
    backgroundColor: primary[600],
  },
  ".MuiListItemText-root": {
    opacity: collapsed ? 0 : 1,
    margin: 0,
    transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    "& .MuiTypography-root": {
      fontSize: rem(14),
      lineHeight: lineHeight(20),
      fontWeight: 600,
    },
  },
  ".MuiListItemIcon-root": {
    display: "grid",
    placeItems: "center",
    minWidth: "20px",
    minHeight: "20px",
    color: active ? base.White : primary[100],
    "& > .MuiSvgIcon-root": {
      fill: "none",
    },
  },
  ".MuiListItemText-root + .MuiSvgIcon-root": {
    opacity: collapsed ? 0 : 1,
    fontSize: rem(14),
    lineHeight: lineHeight(20),
    fill: "none",
    color: open ? primary[100] : primary[300],
    transform: open ? "rotate(90deg)" : "none",
    transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  },
}));

export const UserWrapper = styled(Box)(() => ({
  marginLeft: "8px",
  marginRight: "8px",
  paddingLeft: "2px",
  paddingRight: "2px",
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  columnGap: "12px",
}));

export const UserInfo = styled(Box)<{ collapsed: boolean }>(
  ({ collapsed }) => ({
    flex: 1,
    width: collapsed ? "0px" : "unset",
    opacity: collapsed ? 0 : 1,
    transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    display: "flex",
    flexDirection: "column",

    ".MuiTypography-root": {
      display: "-webkit-box",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "-webkit-box-orient": "vertical",
      "-webkit-line-clamp": "1",
      fontSize: rem(14),
      lineHeight: lineHeight(20),

      "&:first-of-type": {
        color: base.White,
        fontWeight: 600,
      },
      "&:last-of-type": {
        color: primary[200],
      },
    },
  })
);

export const LogoutButton = styled(IconButton)<{ collapsed: boolean }>(
  ({ collapsed }) => ({
    width: "36px",
    height: "36px",
    flexBasis: "36px",
    color: primary[300],
    opacity: collapsed ? 0 : 1,
    transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  })
);

export const Divider = styled(AppDivider)(() => ({
  backgroundImage:
    "linear-gradient(to right, rgba(52, 71, 103, 0), rgb(255, 255, 255), rgba(52, 71, 103, 0)) !important",
}));

export const Flag = styled("img")(() => ({
  maxWidth: "20px",
  maxHeight: "20px",
  width: "100%",
  height: "100%",
}));
